import { useCallback, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  buildIndividualRows,
  cellState,
  filterIndividualRows,
  INDIVIDUAL_ROLE_COLUMNS,
  INDIVIDUAL_ROLE_LABELS,
  roleChangeConfirmMessage,
  roleCounts,
  SUPPORT_NOT_ASSIGNABLE_NOTE,
  type IndividualRoleColumn,
  type IndividualRow,
} from "../data/individual-permissions";
import { fetchMembers, updateMemberRole, type MemberRow } from "../data/members";
import { useReadModel } from "../data/useReadModel";

// PRD-001AE AE4: Individual Permissions.
//
// One membership role per person, assigned through `update_account_member_role`, the exact call
// the Members directory has always made. There is NO per-user permission key here and no second
// ACL (AC-001AE.4): what a role may do is Group Permissions, who holds it is this grid.
//
// Runtime policy panels (duty tracking, mobile flags, alert channels) are elsewhere on Admin and
// are not permissions (AC-001AE.5). Nothing on this surface touches them.

/** The directory page size. A full page means the roster may be truncated. */
const MEMBER_PAGE_LIMIT = 100;

export function IndividualPermissionsPanel({
  client,
  accountId,
  canView = false,
  canManage = false,
  onPermissionsChanged,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canView?: boolean;
  canManage?: boolean;
  /** Re-read the caller's own capability keys so nav gates follow a role change. */
  onPermissionsChanged?: () => void;
  pollMs?: number;
}) {
  if (!canView) {
    return null;
  }
  return (
    <IndividualPermissionsPanelInner
      client={client}
      accountId={accountId}
      canManage={canManage}
      {...(onPermissionsChanged !== undefined ? { onPermissionsChanged } : {})}
      {...(pollMs !== undefined ? { pollMs } : {})}
    />
  );
}

function IndividualPermissionsPanelInner({
  client,
  accountId,
  canManage,
  onPermissionsChanged,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  onPermissionsChanged?: () => void;
  pollMs?: number;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const [query, setQuery] = useState("");
  const [busyMembershipId, setBusyMembershipId] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  /** A move is armed before it runs, in panel, matching the Group tab and the Capacitor shell. */
  const [pending, setPending] = useState<{
    readonly membershipId: string;
    readonly next: IndividualRoleColumn;
  } | null>(null);

  const { status, data, error } = useReadModel<MemberRow[]>(
    () => fetchMembers(client, accountId, MEMBER_PAGE_LIMIT),
    [client, accountId, reloadNonce],
    pollMs !== undefined ? { pollMs } : {},
  );

  const rows = useMemo(() => buildIndividualRows(data ?? []), [data]);
  const visible = useMemo(() => filterIndividualRows(rows, query), [rows, query]);
  const counts = useMemo(() => roleCounts(rows), [rows]);
  // A full page may have been cut off, which would make the admin count wrong. The last-admin
  // lock is skipped then and the host stays the only control.
  const rosterComplete = (data?.length ?? 0) < MEMBER_PAGE_LIMIT;
  const cellContext = useMemo(() => ({ rows, rosterComplete }), [rows, rosterComplete]);

  const pendingMove = useMemo(() => {
    if (pending === null) return null;
    const row = rows.find((candidate) => candidate.membershipId === pending.membershipId);
    // A reload that dropped the member drops the armed confirm with it, rather than leaving a
    // button that would act on somebody no longer on screen.
    return row === undefined ? null : { row, next: pending.next };
  }, [pending, rows]);

  const applyMove = useCallback(
    async (row: IndividualRow, next: IndividualRoleColumn) => {
      if (!canManage || busyMembershipId !== null) return;
      setPending(null);
      setBusyMembershipId(row.membershipId);
      setFailure(null);
      setReceipt(null);
      const outcome = await updateMemberRole(client, accountId, row.membershipId, next);
      setBusyMembershipId(null);
      if (!outcome.ok) {
        setFailure(outcome.reason);
        return;
      }
      setReceipt(`${row.label} is now ${INDIVIDUAL_ROLE_LABELS[next]}.`);
      setReloadNonce((nonce) => nonce + 1);
      // A role change can change what the EDITOR may do when they moved themselves through some
      // other path, and it always changes the nav for the person moved. Re-read either way.
      onPermissionsChanged?.();
    },
    [accountId, busyMembershipId, canManage, client, onPermissionsChanged],
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading the roster...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load the roster."}
      </p>
    );
  }
  if (rows.length === 0) {
    return <p className="surface__state" role="status">No members yet.</p>;
  }

  return (
    <div className="operator-panel individual-permissions">
      <p className="individual-permissions__intro">
        Who holds which role. A member has exactly one role, and moving them changes what they can
        do on the phone and in the console. What each role is allowed to do is the Group
        permissions tab.
      </p>

      <label className="individual-permissions__search">
        <span className="individual-permissions__search-label">Search members</span>
        <input
          type="search"
          value={query}
          placeholder="Name or reference"
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>

      <p className="individual-permissions__count" role="status">
        {INDIVIDUAL_ROLE_COLUMNS.filter((column) => counts[column] > 0)
          .map((column) => `${counts[column]} ${INDIVIDUAL_ROLE_LABELS[column]}`)
          .join(", ")}
        {rows.length === visible.length ? "" : ` (showing ${visible.length} of ${rows.length})`}
      </p>

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}

      {pendingMove !== null ? (
        <div className="individual-permissions__confirm" role="alertdialog" aria-label="Confirm role change">
          <p className="individual-permissions__confirm-text">
            {roleChangeConfirmMessage(pendingMove.row, pendingMove.next)}
          </p>
          <div className="individual-permissions__confirm-actions">
            <button
              type="button"
              className="minter__confirm-go"
              onClick={() => void applyMove(pendingMove.row, pendingMove.next)}
            >
              Move to {INDIVIDUAL_ROLE_LABELS[pendingMove.next]}
            </button>
            <button type="button" onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {visible.length === 0 ? (
        <p className="surface__state" role="status">No member matches that search.</p>
      ) : (
        <div className="individual-permissions__scroll">
          <table className="commercial__table individual-permissions__table">
            <caption className="individual-permissions__caption">Membership role by member</caption>
            <thead>
              <tr>
                <th scope="col">Member</th>
                {INDIVIDUAL_ROLE_COLUMNS.map((column) => (
                  <th key={column} scope="col">
                    {INDIVIDUAL_ROLE_LABELS[column]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.membershipId}>
                  <th scope="row">
                    <span className="individual-permissions__member">{row.label}</span>
                    {row.status === "active" ? null : (
                      <span className="individual-permissions__member-status">{row.status}</span>
                    )}
                  </th>
                  {INDIVIDUAL_ROLE_COLUMNS.map((column) => {
                    const state = cellState(row, column, cellContext);
                    const busy = busyMembershipId === row.membershipId;
                    if (state.kind === "held") {
                      return (
                        <td key={column} className="individual-permissions__cell individual-permissions__cell--held">
                          <span aria-label={`${row.label} is ${INDIVIDUAL_ROLE_LABELS[column]}`}>
                            {busy ? "..." : "Current"}
                          </span>
                        </td>
                      );
                    }
                    if (state.kind === "locked" || !canManage) {
                      return (
                        <td key={column} className="individual-permissions__cell">
                          <span
                            className="individual-permissions__locked"
                            title={state.kind === "locked" ? state.reason : undefined}
                            aria-hidden="true"
                          >
                            ·
                          </span>
                        </td>
                      );
                    }
                    return (
                      <td key={column} className="individual-permissions__cell">
                        <button
                          type="button"
                          className="individual-permissions__assign"
                          aria-label={`Move ${row.label} to ${INDIVIDUAL_ROLE_LABELS[column]}`}
                          disabled={busyMembershipId !== null}
                          onClick={() => {
                            setFailure(null);
                            setReceipt(null);
                            setPending({ membershipId: row.membershipId, next: column });
                          }}
                        >
                          Move here
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="individual-permissions__note">{SUPPORT_NOT_ASSIGNABLE_NOTE}</p>
      {canManage ? null : (
        <p className="individual-permissions__note">
          Read only. Member management permission is required to move anyone.
        </p>
      )}
      {rosterComplete ? null : (
        <p className="individual-permissions__note">
          Showing the first {MEMBER_PAGE_LIMIT} members. Use the Members list above for the full
          roster.
        </p>
      )}
    </div>
  );
}
